import { useEffect, useMemo, useRef } from 'react';
import { t } from 'i18next';
import { buildWorkspaceCustomFontsUrlsMap, useWorkflowFontsQuery } from '@/queries/useWorkflowFonts';
import { FF_WORKSPACE_CUSTOM_FONTS } from '@/consts/featureFlags';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { I18N_KEYS } from '@/language/keys';
import { useGlobalStore } from '@/state/global.state';
import { useWorkflowStore } from '@/state/workflow.state';
import type { UseQueryResult } from '@tanstack/react-query';
import type { WorkspaceCustomFontDto } from '@/api/recipes';

function workspaceFontsLoadingState(
  queryEnabled: boolean,
  query: Pick<UseQueryResult<WorkspaceCustomFontDto[], Error>, 'isError' | 'isSuccess'>,
): 'loading' | 'loaded' | 'error' {
  if (!queryEnabled) {
    return 'loaded';
  }
  if (query.isError) {
    return 'error';
  }
  if (query.isSuccess) {
    return 'loaded';
  }
  return 'loading';
}

/**
 * Loads workspace custom font metadata for the workflow shell: feature gating, `workspace-fonts` loading state, and a presigned URL map keyed by graph font key (`originalFileName`).
 *
 * Fetching is implemented by {@link useWorkflowFontsQuery}.
 */
export function useWorkflowWorkspaceFonts(recipeId: string | undefined) {
  const { value: isWorkspaceCustomFontsEnabled } = useFeatureFlag(FF_WORKSPACE_CUSTOM_FONTS);
  const setLoadingState = useWorkflowStore((s) => s.setLoadingState);
  const queryEnabled = Boolean(recipeId) && isWorkspaceCustomFontsEnabled === true;
  const { data, isError, isSuccess, errorUpdatedAt } = useWorkflowFontsQuery(recipeId, { enabled: queryEnabled });
  const lastSnackbarErrorUpdatedAt = useRef(0);

  useEffect(() => {
    setLoadingState('workspace-fonts', workspaceFontsLoadingState(queryEnabled, { isError, isSuccess }));

    if (!queryEnabled || !isError) {
      lastSnackbarErrorUpdatedAt.current = 0;
      return;
    }
    if (errorUpdatedAt === lastSnackbarErrorUpdatedAt.current) {
      // prevent duplicate snackbar on same error
      return;
    }
    lastSnackbarErrorUpdatedAt.current = errorUpdatedAt;
    // show error snackbar
    useGlobalStore.getState().updateSnackbarData({
      text: t(I18N_KEYS.FLOW_WARNINGS.FAILED_LOAD_WORKSPACE_CUSTOM_FONTS),
      icon: null,
      isOpen: true,
      duration: 5000,
      severity: 'warning',
    });
  }, [errorUpdatedAt, isError, isSuccess, queryEnabled, setLoadingState]);

  const workspaceCustomFontsUrlsMap = useMemo(() => buildWorkspaceCustomFontsUrlsMap(data ?? []), [data]);

  return {
    workspaceCustomFontsUrlsMap,
  };
}
